import React, { useState, useEffect } from 'react';
import { Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Row, Button, Col, Spinner } from 'reactstrap';
import { ChevronUp, ChevronDown, Activity, Video, ClipboardList, RefreshCw } from 'lucide-react';
import RecordingCard from './RecordingCard';
import MeetingCard from './MeetingCard';
import SelfReportCard from './SelfReportCard';
import axios from 'axios';

const DashboardStatistics = ({ selectedSubject, baseUrl }) => {
    const [isStatsOpen, setIsStatsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [recordings, setRecordings] = useState([]);
    const [isLoading, setIsLoading] = useState({
        recordings: false,
        meetings: false,
        selfReports: false

    });
    const [error, setError] = useState({
        recordings: null,
        meetings: null,
        selfReports: null
    }); const [lastEvaluatedRecordingKey, setLastEvaluatedRecordingKey] = useState(null);
    const [lastEvaluatedMeetingKey, setLastEvaluatedMeetingKey] = useState(null);
    const [meetings, setMeetings] = useState([]);
    const [selfReports, setSelfReports] = useState([]);

    const fetchSelfReports = async () => {
        if (!selectedSubject?.subjectId) return;

        setIsLoading(prev => ({ ...prev, selfReports: true }));
        setError(prev => ({ ...prev, selfReports: null }));

        try {
            const response = await axios.post(`${baseUrl}web/patientSelfReportMeasurements`, {
                patientId: selectedSubject.subjectId
            });
            setSelfReports(response.data.selfReports || []);
        } catch (error) {
            console.error('Error fetching self reports:', error);
            setError(prev => ({
                ...prev,
                selfReports: 'Failed to load self reports. Please try again.'
            }));
        } finally {
            setIsLoading(prev => ({ ...prev, selfReports: false }));
        }
    };

    const fetchRecordings = async (isNewSearch = true) => {
        if (!selectedSubject?.subjectId) return;

        setIsLoading(prev => ({ ...prev, recordings: true }));
        setError(prev => ({ ...prev, recordings: null }));

        try {
            const request = {
                timeFrame: {
                    start: Math.floor(Date.now() / 1000 - (14 * 24 * 60 * 60)), // Last 14 days
                    end: Math.floor(Date.now() / 1000)
                },
                patientId: selectedSubject.subjectId,
                lastEvaluatedKey: isNewSearch ? null : lastEvaluatedRecordingKey
            };

            const response = await axios.post(`${baseUrl}web/patientRecordings`, request);
            if (isNewSearch) {
                setRecordings(response.data.recordings);
            } else {
                setRecordings(prevRecordings => [...prevRecordings, ...response.data.recordings]);
            }

            setLastEvaluatedRecordingKey(response.data.lastEvaluatedKey);
        } catch (error) {
            console.error('Error fetching recordings:', error);
            setError(prev => ({
                ...prev,
                recordings: 'Failed to load recordings. Please try again.'
            }));
        } finally {
            setIsLoading(prev => ({ ...prev, recordings: false }));
        }
    };

    const fetchMeetings = async (isNewSearch = true) => {
        if (!selectedSubject?.subjectId) return;

        setIsLoading(prev => ({ ...prev, meetings: true }));
        setError(prev => ({ ...prev, meetings: null }));

        try {
            const request = {
                timeFrame: {
                    start: Math.floor(Date.now() / 1000 - (14 * 24 * 60 * 60)), // Last 14 days
                    end: Math.floor(Date.now() / 1000)
                },
                patientId: selectedSubject.subjectId,
                lastEvaluatedKey: isNewSearch ? null : lastEvaluatedMeetingKey

            };

            const response = await axios.post(`${baseUrl}web/patientTeleconferenceMeetings`, request);
            if (isNewSearch) {
                setMeetings(response.data.meetings);
            } else {
                setMeetings(prevMeetings => [...prevMeetings, ...response.data.meetings]);
            }
            setLastEvaluatedMeetingKey(response.data.lastEvaluatedKey);

        } catch (error) {
            console.error('Error fetching meetings:', error);
            setError(prev => ({
                ...prev,
                meetings: 'Failed to load meetings. Please try again.'
            }));
        } finally {
            setIsLoading(prev => ({ ...prev, meetings: false }));
        }
    };

    const handleLoadMoreRecording = () => {
        if (lastEvaluatedRecordingKey) {
            fetchRecordings(false);
        }
    };

    const handleLoadMoreMeeting = () => {
        if (lastEvaluatedMeetingKey) {
            fetchMeetings(false);
        }
    };

    const handleNoteAdded = (recordingId, updatedNotes) => {
        setRecordings(prevRecordings =>
            prevRecordings.map(recording =>
                recording.recordingId === recordingId
                    ? { ...recording, notes: updatedNotes }
                    : recording
            )
        );
    };


    useEffect(() => {
        if (!isStatsOpen) return;

        if (activeTab === '1' && recordings.length === 0) {
            fetchRecordings();
        } else if (activeTab === '2' && meetings.length === 0) {
            fetchMeetings();
        } else if (activeTab === '3' && selfReports.length === 0) {
            fetchSelfReports();
        }
    }, [isStatsOpen, activeTab]);

    const handleToggle = () => {
        setIsStatsOpen(!isStatsOpen);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const StatCard = ({ title, value, bgColor = 'bg-primary', textColor = 'text-primary' }) => (
        <Card className={`border-0 ${bgColor} bg-opacity-10`}>
            <CardBody>
                <h6 className="text-muted">{title}</h6>
                <h3 className={`mb-0 ${textColor}`}>{value}</h3>
            </CardBody>
        </Card>
    );

    const renderRecordingsContent = () => {
        if (isLoading.recordings) {
            return (
                <div className="text-center py-4">
                    <Spinner color="primary" size="sm" className="me-2" />
                    Loading recordings...
                </div>
            );
        }

        if (error.recordings) {
            return (
                <div className="text-center py-4 bg-light rounded">
                    <div className="text-danger mb-3">{error.recordings}</div>
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={fetchRecordings}
                    >
                        <RefreshCw size={14} className="me-1" />
                        Retry
                    </button>
                </div>
            );
        }

        if (recordings.length === 0) {
            return (
                <div className="text-center py-4 bg-light rounded">
                    <Activity size={24} className="text-muted mb-2" />
                    <p className="text-muted mb-0">No recent recordings available</p>
                </div>
            );
        }

        return (
            <div className="recordings-list">
                {recordings.map((recording) => (
                    <RecordingCard
                        key={recording.recordingId}
                        onNoteAdded={handleNoteAdded}
                        recording={recording}
                        baseUrl={baseUrl}
                    />
                ))}
                {lastEvaluatedRecordingKey && (
                    <Button color="secondary" onClick={handleLoadMoreRecording} disabled={isLoading.recordings}>
                        {isLoading.recordings ? <Spinner size="sm" /> : 'Load More'}
                    </Button>
                )}
            </div>
        );
    };

    return (
        <Card className="mb-4 border-0 shadow-sm">
            <CardBody>
                <div
                    className="d-flex align-items-center"
                    onClick={handleToggle}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="d-flex align-items-center gap-4 flex-grow-1">
                        <h5 className="mb-0">Patient Statistics</h5>

                        {!isStatsOpen && (
                            <>
                                <div className="d-flex align-items-center border-start ps-4">
                                    <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-2">
                                        <Activity size={16} className="text-primary" />
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Today</small>
                                        <span className="fs-5 fw-semibold">{selectedSubject.todayRecordings}</span>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center border-start ps-4">
                                    <div className="rounded-circle bg-info bg-opacity-10 p-2 me-2">
                                        <Activity size={16} className="text-info" />
                                    </div>
                                    <div>
                                        <small className="text-muted d-block">Total</small>
                                        <span className="fs-5 fw-semibold">{selectedSubject.allTimeRecordings}</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    {isStatsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>

                {isStatsOpen && (
                    <div className="mt-4">
                        <Nav tabs className="nav-tabs-custom mb-3">
                            <NavItem>
                                <NavLink
                                    className={`px-3 ${activeTab === '1' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('1')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="d-flex align-items-center">
                                        <Activity size={16} className="me-2" />
                                        Recordings
                                    </span>
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={`px-3 ${activeTab === '2' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('2')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="d-flex align-items-center">
                                        <Video size={16} className="me-2" />
                                        Teleconference Meetings
                                    </span>
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={`px-3 ${activeTab === '3' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('3')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="d-flex align-items-center">
                                        <ClipboardList size={16} className="me-2" />
                                        Self Report
                                    </span>
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab} className="p-3">
                            <TabPane tabId="1">
                                <Row className="g-3 mb-4">
                                    <Col md={4}>
                                        <StatCard
                                            title="Today's Recordings"
                                            value={selectedSubject.todayRecordings}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <StatCard
                                            title="Total Recordings"
                                            value={selectedSubject.allTimeRecordings}
                                            bgColor="bg-info"
                                            textColor="text-info"
                                        />
                                    </Col>
                                </Row>

                                {renderRecordingsContent()}
                            </TabPane>

                            <TabPane tabId="2">
                                <Row className="g-3 mb-4">
                                    <Col md={4}>
                                        <StatCard
                                            title="Today's Meetings"
                                            value={meetings.filter(m => {
                                                const meetingDate = new Date(m.meetingCreatedAt * 1000);
                                                const today = new Date();
                                                return meetingDate.toDateString() === today.toDateString();
                                            }).length}
                                            bgColor="bg-info"
                                            textColor="text-info"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <StatCard
                                            title="Total Meetings"
                                            value={meetings.length}
                                            bgColor="bg-primary"
                                            textColor="text-primary"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <StatCard
                                            title="Active Meetings"
                                            value={meetings.filter(m => m.meetingStatus === 'SET').length}
                                            bgColor="bg-success"
                                            textColor="text-success"
                                        />
                                    </Col>
                                </Row>

                                {isLoading.meetings ? (
                                    <div className="text-center py-4">
                                        <Spinner color="primary" size="sm" className="me-2" />
                                        Loading meetings...
                                    </div>
                                ) : error.meetings ? (
                                    <div className="text-center py-4 bg-light rounded">
                                        <div className="text-danger mb-3">{error.meetings}</div>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={fetchMeetings}
                                        >
                                            <RefreshCw size={14} className="me-1" />
                                            Retry
                                        </button>
                                    </div>
                                ) : meetings.length === 0 ? (
                                    <div className="text-center py-4 bg-light rounded">
                                        <Video size={24} className="text-muted mb-2" />
                                        <p className="text-muted mb-0">No meetings found</p>
                                    </div>
                                ) : (
                                    <div className="meetings-list">
                                        {meetings.map((meeting) => (
                                            <MeetingCard
                                                key={meeting.meetingId}
                                                meeting={meeting}
                                                baseUrl={baseUrl}
                                                doctorJoinTime={meeting.doctorJoinTime}
                                                doctorExitTime={meeting.doctorExitTime}
                                                patientJoinTime={meeting.patientJoinTime}
                                                patientExitTime={meeting.patientExitTime}
                                            />
                                        ))}
                                        {lastEvaluatedMeetingKey && (
                                            <Button color="secondary" onClick={handleLoadMoreMeeting} disabled={isLoading.meetings}>
                                                {isLoading.meetings ? <Spinner size="sm" /> : 'Load More'}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </TabPane>

                            <TabPane tabId="3">
                                <Row className="g-3 mb-4">
                                    <Col md={4}>
                                        <StatCard
                                            title="Today's Reports"
                                            value={selfReports.filter(r => {
                                                const reportDate = new Date(r.createdAt * 1000);
                                                const today = new Date();
                                                return reportDate.toDateString() === today.toDateString();
                                            }).length}
                                            bgColor="bg-info"
                                            textColor="text-info"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <StatCard
                                            title="Total Reports"
                                            value={selfReports.length}
                                            bgColor="bg-primary"
                                            textColor="text-primary"
                                        />
                                    </Col>

                                </Row>

                                {isLoading.selfReports ? (
                                    <div className="text-center py-4">
                                        <Spinner color="primary" size="sm" className="me-2" />
                                        Loading self reports...
                                    </div>
                                ) : error.selfReports ? (
                                    <div className="text-center py-4 bg-light rounded">
                                        <div className="text-danger mb-3">{error.selfReports}</div>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={fetchSelfReports}
                                        >
                                            <RefreshCw size={14} className="me-1" />
                                            Retry
                                        </button>
                                    </div>
                                ) : selfReports.length === 0 ? (
                                    <div className="text-center py-4 bg-light rounded">
                                        <ClipboardList size={24} className="text-muted mb-2" />
                                        <p className="text-muted mb-0">No self reports found</p>
                                    </div>
                                ) : (
                                    <div className="self-reports-list">
                                        {selfReports.slice(0, 3).map((report) => (
                                            <SelfReportCard
                                                key={report.measurementId}
                                                report={report}
                                                baseUrl={baseUrl}
                                                patientId={selectedSubject.subjectId}
                                            />
                                        ))}
                                    </div>
                                )}
                            </TabPane>
                        </TabContent>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default DashboardStatistics;